/**
 * @file MenuTree.tsx
 * @description Дерево меню одного места с перетаскиванием (phase19.16 Р1;
 * SB15; uiux-design-r1 §3; донор — @dnd-kit, как в questionnaire и
 * конструкторе страниц).
 *
 * Перетаскивание меняет порядок среди соседей: пункты верхнего уровня —
 * между собой (вложенные едут вместе с родителем), вложенные — внутри
 * своего родителя. Вложить или вынести — через «⋯» (третьего уровня нет).
 * При шапке «логотип по центру» между пунктами видна строка «здесь логотип».
 */

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";

import type { RowAction } from "../../ui/RowActionsMenu";
import { MenuItemRow } from "./MenuItemRow";
import { locate, moveSibling } from "../../../lib/site-view/menu-tree";
import type { SiteMenuItem } from "../../../lib/site-view/types";

interface Props {
  items: SiteMenuItem[];
  actionsFor: (item: SiteMenuItem, nested: boolean) => RowAction[];
  onReorder: (tree: SiteMenuItem[]) => void;
  /** Позиция строки «здесь логотип» (после N пунктов верхнего уровня); `null` — нет строки. */
  logoAfter: number | null;
}

function LogoDivider() {
  return (
    <div className="type-caption flex min-h-10 items-center gap-2 px-3 text-on-surface-variant" aria-label="Здесь логотип">
      <span className="h-px flex-1 border-t border-dashed border-on-surface/30" />
      здесь логотип (шапка «по центру»)
      <span className="h-px flex-1 border-t border-dashed border-on-surface/30" />
    </div>
  );
}

export function MenuTree({ items, actionsFor, onReorder, logoAfter }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = locate(items, String(active.id));
    const to = locate(items, String(over.id));
    // Только среди соседей: перенос в другой родитель — через «⋯».
    if (!from || !to || from.parent !== to.parent) return;
    const next = moveSibling(items, String(active.id), to.index);
    if (next) onReorder(next);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col divide-y divide-[var(--border)] overflow-hidden rounded-xl bg-surface-lowest [box-shadow:inset_0_0_0_1px_var(--border)]">
          {items.map((item, index) => (
            <div key={item.id} className="flex flex-col">
              {logoAfter === index ? <LogoDivider /> : null}
              <MenuItemRow item={item} nested={false} actions={actionsFor(item, false)} />
              {item.children.length > 0 ? (
                <SortableContext items={item.children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                  {item.children.map((child) => (
                    <MenuItemRow key={child.id} item={child} nested actions={actionsFor(child, true)} />
                  ))}
                </SortableContext>
              ) : null}
            </div>
          ))}
          {logoAfter !== null && logoAfter >= items.length && items.length > 0 ? <LogoDivider /> : null}
        </div>
      </SortableContext>
    </DndContext>
  );
}
